import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Tab,
  Tabs,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  QrCode as QrCodeIcon,
  TableBar as TableBarIcon,
  Print as PrintIcon,
  AccessTime as AccessTimeIcon,
  PointOfSale as PointOfSaleIcon,
  EventAvailable as EventAvailableIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Login as LoginIcon,
  Link as LinkIcon,
  SmokingRooms as SmokingRoomsIcon,
  Weekend as WeekendIcon,
  ChildCare as ChildCareIcon,
  Accessible as AccessibleIcon,
  Window as WindowIcon,
  ContentCopy as ContentCopyIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { collection, getDocs, query, where, addDoc, updateDoc, deleteDoc, doc, Timestamp, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Table, Printer, Reservation, SeatType } from '../types';
import { nowInShopTimezone } from '../utils/businessDate';
import { tableOrderUrl } from '../utils/siteUrls';

interface StaffMember {
  id: string;
  name: string;
}

// 席種別の定義
const SEAT_TYPES: { value: SeatType; label: string }[] = [
  { value: 'table', label: 'テーブル席' },
  { value: 'counter', label: 'カウンター' },
  { value: 'privateRoom', label: '個室' },
  { value: 'semiPrivate', label: '半個室' },
  { value: 'tatami', label: '座敷' },
  { value: 'terrace', label: 'テラス' },
  { value: 'other', label: 'その他' },
];

// 席の特徴の定義
const SEAT_FEATURES: { value: string; label: string; icon: React.ReactNode }[] = [
  { value: 'smoking', label: '喫煙可', icon: <SmokingRoomsIcon fontSize="small" /> },
  { value: 'window', label: '窓際', icon: <WindowIcon fontSize="small" /> },
  { value: 'kids', label: '子連れOK', icon: <ChildCareIcon fontSize="small" /> },
  { value: 'wheelchair', label: '車椅子対応', icon: <AccessibleIcon fontSize="small" /> },
  { value: 'horigotatsu', label: '掘りごたつ', icon: <WeekendIcon fontSize="small" /> },
];

interface Order {
  id: string;
  tableId: string;
  tableNumber: string;
  items: {
    productName: string;
    quantity: number;
    subtotal: number;
  }[];
  total: number;
  status: string;
  orderedAt: Date;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const formatElapsedTime = (startTime: Date | null): string => {
  if (!startTime || !(startTime instanceof Date) || isNaN(startTime.getTime())) return '-';
  const now = new Date();
  const diff = now.getTime() - startTime.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}分`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}時間${remainingMinutes}分`;
};

const getElapsedTimeColor = (startTime: Date | null): string => {
  if (!startTime || !(startTime instanceof Date) || isNaN(startTime.getTime())) return 'text.secondary';
  const now = new Date();
  const diff = now.getTime() - startTime.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return 'success.main';
  if (minutes < 120) return 'warning.main';
  return 'error.main';
};

const Tables: React.FC = () => {
  const { shop } = useAuth();
  const navigate = useNavigate();
  const [tables, setTables] = useState<Table[]>([]);
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(''); // YYYY-MM-DD（予約表示日付）
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [timer, setTimer] = useState(0); // 経過時間更新用
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openQRDialog, setOpenQRDialog] = useState(false);
  const [openAssignDialog, setOpenAssignDialog] = useState(false); // テーブルアサインダイアログ
  const [editingTable, setEditingTable] = useState<Table | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Table | null>(null);
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [assignReservation, setAssignReservation] = useState<Reservation | null>(null); // アサイン対象の予約
  const [selectedAssignTableId, setSelectedAssignTableId] = useState<string>('');
  const [openCheckoutConfirmDialog, setOpenCheckoutConfirmDialog] = useState(false); // 会計確認ダイアログ
  const [checkoutTargetTable, setCheckoutTargetTable] = useState<Table | null>(null); // 会計対象テーブル
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // スタッフ一覧（担当者選択用）
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  // 来店ダイアログ
  const [openCheckInDialog, setOpenCheckInDialog] = useState(false);
  const [checkInReservation, setCheckInReservation] = useState<Reservation | null>(null);
  const [checkInTableId, setCheckInTableId] = useState<string>('');
  const [checkInStaffId, setCheckInStaffId] = useState<string>('');
  const [checkInStaffName, setCheckInStaffName] = useState<string>('');
  const [formData, setFormData] = useState({
    tableNumber: '',
    tableName: '',
    seatType: 'table' as SeatType,
    minCapacity: 1,
    maxCapacity: 4,
    seats: 4,
    sortOrder: 0,
    features: [] as string[],
    description: '',
    additionalFee: '',
    isActive: true,
    // 結合設定
    canCombine: false,
    combinableWith: [] as string[],
    combine2MinCapacity: undefined as number | undefined,
    combine2MaxCapacity: undefined as number | undefined,
    combine3PlusMinCapacity: undefined as number | undefined,
    combine3PlusMaxCapacity: undefined as number | undefined,
  });

  // selectedDate初期化（shop timezone考慮）
  useEffect(() => {
    if (shop && !selectedDate) {
      const shopNow = nowInShopTimezone(shop.timezone);
      const y = shopNow.getFullYear();
      const m = String(shopNow.getMonth() + 1).padStart(2, '0');
      const d = String(shopNow.getDate()).padStart(2, '0');
      setSelectedDate(`${y}-${m}-${d}`);
    }
  }, [shop]);

  // スタッフ一覧を取得
  useEffect(() => {
    if (!shop) return;
    const q = query(
      collection(db, 'employees'),
      where('shopId', '==', shop.id)
    );
    const unsub = onSnapshot(q, (snapshot) => {
      const members = snapshot.docs.map(d => ({
        id: d.id,
        name: d.data().name || d.data().displayName || d.data().email || d.id,
      }));
      setStaffMembers(members);
    });
    return () => unsub();
  }, [shop]);

  useEffect(() => {
    if (shop) {
      fetchTables();
      fetchPrinters();

      // テーブルをリアルタイム監視
      const tablesQuery = query(
        collection(db, 'tables'),
        where('shopId', '==', shop.id)
      );
      const unsubTables = onSnapshot(tablesQuery, (snapshot) => {
        const data = snapshot.docs.map(doc => {
          const docData = doc.data();
          // sessionStartedAtの安全な変換（Timestampでない場合も対応）
          let sessionStartedAt: Date | undefined;
          if (docData.sessionStartedAt) {
            if (typeof docData.sessionStartedAt.toDate === 'function') {
              sessionStartedAt = docData.sessionStartedAt.toDate();
            } else if (docData.sessionStartedAt instanceof Date) {
              sessionStartedAt = docData.sessionStartedAt;
            } else if (typeof docData.sessionStartedAt === 'number') {
              sessionStartedAt = new Date(docData.sessionStartedAt);
            }
          }
          return {
            id: doc.id,
            ...docData,
            createdAt: docData.createdAt?.toDate(),
            updatedAt: docData.updatedAt?.toDate(),
            sessionStartedAt,
          };
        }) as Table[];
        data.sort((a, b) => {
          const aSort = (a as any).sortOrder ?? 9999;
          const bSort = (b as any).sortOrder ?? 9999;
          if (aSort !== bSort) return aSort - bSort;
          return a.tableNumber.localeCompare(b.tableNumber, 'ja', { numeric: true });
        });
        setTables(data);
        setLoading(false);
      });

      // 注文をリアルタイム監視
      const ordersQuery = query(
        collection(db, 'orders'),
        where('shopId', '==', shop.id),
        where('status', 'in', ['pending', 'confirmed', 'preparing', 'ready', 'served'])
      );
      const unsubOrders = onSnapshot(ordersQuery, (snapshot) => {
        const data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          orderedAt: doc.data().orderedAt?.toDate(),
        })) as Order[];
        setOrders(data);
      });

      // 経過時間を1分ごとに更新
      const interval = setInterval(() => {
        setTimer(t => t + 1);
      }, 60000);

      return () => {
        unsubTables();
        unsubOrders();
        clearInterval(interval);
      };
    }
  }, [shop]);

  // 予約をリアルタイム監視（選択日付に連動）
  useEffect(() => {
    if (!shop || !selectedDate) return;

    const [year, month, day] = selectedDate.split('-').map(Number);
    const dayStart = new Date(year, month - 1, day);
    const dayEnd = new Date(year, month - 1, day + 1);

    const reservationsQuery = query(
      collection(db, 'reservations'),
      where('shopId', '==', shop.id),
      where('reservationDate', '>=', Timestamp.fromDate(dayStart)),
      where('reservationDate', '<', Timestamp.fromDate(dayEnd))
    );
    const unsubReservations = onSnapshot(reservationsQuery, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        reservationDate: doc.data().reservationDate?.toDate(),
        checkedInAt: doc.data().checkedInAt?.toDate(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Reservation[];
      // confirmed と pending のみ表示（cancelled, completed, no_show は除外）
      const activeReservations = data.filter(r => ['confirmed', 'pending'].includes(r.status));
      // 開始時間でソート
      activeReservations.sort((a, b) => a.startTime.localeCompare(b.startTime));
      setReservations(activeReservations);
    });

    return () => unsubReservations();
  }, [shop, selectedDate]);

  const fetchTables = async () => {
    if (!shop) return;

    try {
      const q = query(
        collection(db, 'tables'),
        where('shopId', '==', shop.id)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Table[];
      // テーブル番号でソート
      data.sort((a, b) => {
        const aNum = parseInt(a.tableNumber.replace(/\D/g, '')) || 0;
        const bNum = parseInt(b.tableNumber.replace(/\D/g, '')) || 0;
        return aNum - bNum;
      });
      setTables(data);
    } catch (error) {
      console.error('席取得エラー:', error);
      setError('席の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const fetchPrinters = async () => {
    if (!shop) return;

    try {
      const q = query(
        collection(db, 'printers'),
        where('shopId', '==', shop.id),
        where('isActive', '==', true)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Printer[];
      // sortOrderでソート
      data.sort((a, b) => a.sortOrder - b.sortOrder);
      setPrinters(data);
    } catch (error) {
      console.error('プリンター取得エラー:', error);
    }
  };

  const handleOpenDialog = (table?: Table) => {
    if (table) {
      setEditingTable(table);
      setFormData({
        tableNumber: table.tableNumber,
        tableName: table.tableName || '',
        seatType: table.seatType || 'table',
        minCapacity: table.minCapacity || 1,
        maxCapacity: table.maxCapacity || table.seats,
        seats: table.seats,
        sortOrder: (table as any).sortOrder || 0,
        features: table.features || [],
        description: (table as any).description || '',
        additionalFee: table.additionalFee?.toString() || '',
        isActive: table.isActive,
        canCombine: table.canCombine || false,
        combinableWith: table.combinableWith || [],
        combine2MinCapacity: table.combine2MinCapacity,
        combine2MaxCapacity: table.combine2MaxCapacity,
        combine3PlusMinCapacity: table.combine3PlusMinCapacity,
        combine3PlusMaxCapacity: table.combine3PlusMaxCapacity,
      });
    } else {
      setEditingTable(null);
      // 次のテーブル番号を自動提案
      const maxTableNum = tables.length > 0
        ? Math.max(...tables.map(t => parseInt(t.tableNumber.replace(/\D/g, '')) || 0))
        : 0;
      const maxSortOrder = tables.length > 0
        ? Math.max(...tables.map(t => (t as any).sortOrder || 0))
        : 0;
      setFormData({
        tableNumber: String(maxTableNum + 1),
        tableName: '',
        seatType: 'table',
        minCapacity: 1,
        maxCapacity: 4,
        seats: 4,
        sortOrder: maxSortOrder + 1,
        features: [],
        description: '',
        additionalFee: '',
        isActive: true,
        canCombine: false,
        combinableWith: [],
        combine2MinCapacity: undefined,
        combine2MaxCapacity: undefined,
        combine3PlusMinCapacity: undefined,
        combine3PlusMaxCapacity: undefined,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTable(null);
    setError(null);
  };

  // モバイルオーダーURL（実際のリンク先）を生成
  const getMobileOrderUrl = (tableId: string) => {
    // URL は utils/siteUrls.ts が唯一の出どころ。
    // 画面ごとに直接書くと、ドメインを変えたときに
    // 「卓のQRだけ古いドメインのまま」になる。
    return tableOrderUrl(shop?.shopCode ?? '', tableId);
  };

  const generateQRCodeUrl = (tableId: string, tableNumber: string) => {
    // 店舗コードとテーブルIDでQRコード生成
    const orderUrl = getMobileOrderUrl(tableId);
    // QRコード生成サービスを使用（例: qrserver.com）
    return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(orderUrl)}`;
  };

  // URLをクリップボードにコピー
  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setSuccess('URLをコピーしました');
      setTimeout(() => setSuccess(null), 2000);
    }).catch(() => {
      setError('URLのコピーに失敗しました');
    });
  };

  const handleSubmit = async () => {
    if (!shop || !formData.tableNumber) {
      setError('席番号は必須です');
      return;
    }

    // 重複チェック
    const duplicate = tables.find(
      t => t.tableNumber === formData.tableNumber && t.id !== editingTable?.id
    );
    if (duplicate) {
      setError('この席番号は既に使用されています');
      return;
    }

    // 最小人数・最大人数の検証
    // 入力欄の min はブラウザのヒントで、貼り付けや保存は止まらない。
    // -5 人の席、-5000 円の追加料金がそのまま保存されていた。
    // 追加料金がマイナスだと、誤入力が会計で値引きとして通る。
    if (Number(formData.minCapacity) < 1) {
      setError('最小人数は1以上にしてください');
      return;
    }
    if (Number(formData.maxCapacity) < 1) {
      setError('最大人数は1以上にしてください');
      return;
    }
    if (formData.minCapacity > formData.maxCapacity) {
      setError('最小人数は最大人数以下にしてください');
      return;
    }
    const feeValue = formData.additionalFee ? parseFloat(formData.additionalFee) : 0;
    if (!Number.isFinite(feeValue) || feeValue < 0) {
      setError('追加料金は0以上にしてください');
      return;
    }

    try {
      const now = Timestamp.now();
      const tableData: any = {
        shopId: shop.id,
        tableNumber: formData.tableNumber,
        tableName: formData.tableName || null,
        seatType: formData.seatType,
        minCapacity: Number(formData.minCapacity),
        maxCapacity: Number(formData.maxCapacity),
        seats: Number(formData.maxCapacity), // 後方互換性
        sortOrder: Number(formData.sortOrder) || 0,
        features: formData.features,
        description: formData.description || '',
        additionalFee: feeValue,
        isActive: formData.isActive,
        canCombine: formData.canCombine,
        combinableWith: formData.combinableWith,
        updatedAt: now,
      };

      // 結合設定（オプショナル）
      if (formData.canCombine) {
        if (formData.combine2MinCapacity !== undefined) {
          tableData.combine2MinCapacity = Number(formData.combine2MinCapacity);
        }
        if (formData.combine2MaxCapacity !== undefined) {
          tableData.combine2MaxCapacity = Number(formData.combine2MaxCapacity);
        }
        if (formData.combine3PlusMinCapacity !== undefined) {
          tableData.combine3PlusMinCapacity = Number(formData.combine3PlusMinCapacity);
        }
        if (formData.combine3PlusMaxCapacity !== undefined) {
          tableData.combine3PlusMaxCapacity = Number(formData.combine3PlusMaxCapacity);
        }
      }

      if (editingTable) {
        // 更新
        await updateDoc(doc(db, 'tables', editingTable.id), tableData);
        setSuccess('席を更新しました');
      } else {
        // 新規作成
        const docRef = await addDoc(collection(db, 'tables'), {
          ...tableData,
          qrCodeUrl: null, // 後で更新
          createdAt: now,
        });

        // QRコードURLを生成して更新
        const qrCodeUrl = generateQRCodeUrl(docRef.id, formData.tableNumber);
        await updateDoc(doc(db, 'tables', docRef.id), {
          qrCodeUrl,
        });

        setSuccess('席を追加しました');
      }

      handleCloseDialog();
      fetchTables();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('席保存エラー:', error);
      setError('席の保存に失敗しました');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await deleteDoc(doc(db, 'tables', deleteTarget.id));
      setSuccess('席を削除しました');
      setOpenDeleteDialog(false);
      setDeleteTarget(null);
      fetchTables();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('席削除エラー:', error);
      setError('席の削除に失敗しました');
    }
  };

  const handleShowQR = (table: Table) => {
    setSelectedTable(table);
    setOpenQRDialog(true);
  };

  const handlePrintQRBrowser = () => {
    if (!selectedTable) return;

    const orderUrl = getMobileOrderUrl(selectedTable.id);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      // QRコードのSVGをDOM上で取得してHTML文字列にする
      const qrContainer = document.getElementById('qr-code-print-source');
      const svgElement = qrContainer?.querySelector('svg');
      const svgHtml = svgElement ? svgElement.outerHTML : '';

      printWindow.document.write(`
        <html>
          <head>
            <title>QRコード - ${selectedTable.tableName || selectedTable.tableNumber}</title>
            <style>
              body {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                min-height: 100vh;
                margin: 0;
                font-family: Arial, sans-serif;
              }
              h1 { margin: 20px 0; }
              svg { margin: 20px 0; }
              p { margin: 10px 0; color: #666; }
              @media print {
                body { padding: 20px; }
              }
            </style>
          </head>
          <body>
            <h1>${shop?.shopName}</h1>
            <h2>${selectedTable.tableName || selectedTable.tableNumber}</h2>
            ${svgHtml}
            <p>スマートフォンでQRコードをスキャンして注文してください</p>
            <p style="font-size: 12px;">Scan the QR code with your smartphone to order</p>
          </body>
        </html>
      `);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 500);
    }
  };

  // QR印刷ボタン → QRダイアログを表示（ブラウザ印刷へ誘導）
  const handlePrintQR = (table: Table) => {
    setSelectedTable(table);
    setOpenQRDialog(true);
  };

  if (loading) {
    return (
      <Container>
        <Typography>読み込み中...</Typography>
      </Container>
    );
  }

  // テーブル状態別にカウント
  const occupiedTables = tables.filter(t => t.status === 'occupied');
  const reservedTables = tables.filter(t => t.status === 'reserved');
  const availableTables = tables.filter(t => !t.status || t.status === 'available' || t.status === 'cleaning');

  // テーブルごとの注文を取得
  const getOrdersForTable = (tableId: string) => {
    return orders.filter(o => o.tableId === tableId);
  };

  // テーブルごとの注文合計を計算
  const getTableTotal = (tableId: string) => {
    return getOrdersForTable(tableId).reduce((sum, o) => sum + o.total, 0);
  };

  // テーブルにアサインされた予約を取得
  const getReservationForTable = (tableId: string) => {
    return reservations.find(r => r.tableId === tableId);
  };

  // アサインされていない予約を取得
  const unassignedReservations = reservations.filter(r => !r.tableId);

  // 予約者来店ダイアログを開く
  const handleCheckIn = (reservation: Reservation) => {
    setCheckInReservation(reservation);
    setCheckInTableId(reservation.tableId || '');
    // 予約に指名スタッフがいればデフォルトで選択
    setCheckInStaffId((reservation as any).staffId || '');
    setCheckInStaffName((reservation as any).staffName || '');
    setOpenCheckInDialog(true);
  };

  // 来店確定処理
  const handleConfirmCheckIn = async () => {
    if (!checkInReservation) return;
    try {
      await updateDoc(doc(db, 'reservations', checkInReservation.id), {
        checkedInAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      // テーブルを使用中に変更（担当スタッフ情報も保存）
      const tableId = checkInTableId || checkInReservation.tableId;
      if (tableId) {
        const tableUpdate: any = {
          status: 'occupied',
          sessionStartedAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        };
        tableUpdate.primaryStaffId = checkInStaffId || null;
        tableUpdate.primaryStaffName = checkInStaffId ? checkInStaffName : '店';
        await updateDoc(doc(db, 'tables', tableId), tableUpdate);
      }

      setOpenCheckInDialog(false);
      setCheckInReservation(null);
      setSuccess(`${checkInReservation.userName}様の来店を記録しました`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('来店処理エラー:', err);
      setError('来店処理に失敗しました');
    }
  };

  // テーブルアサイン処理
  const handleAssignTable = async () => {
    if (!assignReservation || !selectedAssignTableId) return;

    try {
      const table = tables.find(t => t.id === selectedAssignTableId);

      await updateDoc(doc(db, 'reservations', assignReservation.id), {
        tableId: selectedAssignTableId,
        tableNumber: table?.tableNumber,
        updatedAt: Timestamp.now(),
      });

      // テーブルを予約済みに変更
      await updateDoc(doc(db, 'tables', selectedAssignTableId), {
        status: 'reserved',
        updatedAt: Timestamp.now(),
      });

      setSuccess(`${assignReservation.userName}様を${table?.tableNumber}にアサインしました`);
      setOpenAssignDialog(false);
      setAssignReservation(null);
      setSelectedAssignTableId('');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('席アサインエラー:', err);
      setError('席アサインに失敗しました');
    }
  };

  // 席状態変更
  const handleChangeTableStatus = async (tableId: string, newStatus: string) => {
    try {
      const updateData: any = {
        status: newStatus,
        updatedAt: Timestamp.now(),
      };

      if (newStatus === 'available') {
        updateData.sessionStartedAt = null;
        updateData.primaryStaffId = null;
        updateData.primaryStaffName = null;
      } else if (newStatus === 'occupied') {
        updateData.sessionStartedAt = Timestamp.now();
      }

      await updateDoc(doc(db, 'tables', tableId), updateData);
      setSuccess('席の状態を更新しました');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      console.error('状態変更エラー:', err);
      setError('状態の変更に失敗しました');
    }
  };

  // 会計ボタンの処理
  const handleCheckoutClick = (table: Table) => {
    const tableOrders = getOrdersForTable(table.id);
    if (tableOrders.length > 0) {
      // 注文がある場合は会計画面へ遷移
      navigate(`/mobile-order/checkout?tableId=${table.id}`);
    } else {
      // 注文がない場合は確認ダイアログを表示
      setCheckoutTargetTable(table);
      setOpenCheckoutConfirmDialog(true);
    }
  };

  // 注文なしで会計（席解放）を確定
  const handleConfirmCheckoutWithoutOrders = async () => {
    if (!checkoutTargetTable) return;

    try {
      await updateDoc(doc(db, 'tables', checkoutTargetTable.id), {
        status: 'available',
        sessionStartedAt: null,
        currentSessionId: null,
        updatedAt: Timestamp.now(),
      });
      setSuccess(`${checkoutTargetTable.tableNumber}を空席にしました`);
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      console.error('席解放エラー:', err);
      setError('席の解放に失敗しました');
    } finally {
      setOpenCheckoutConfirmDialog(false);
      setCheckoutTargetTable(null);
    }
  };

  // テーブル状態に応じた色を取得
  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'occupied': return 'error';
      case 'reserved': return 'warning';
      default: return 'success';
    }
  };

  // テーブル状態のラベル
  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'occupied': return '使用中';
      case 'reserved': return '予約済';
      default: return '空席';
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">席管理</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          席を追加
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* 日付ナビゲーション */}
      {selectedDate && (
        <Paper sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => {
              const d = new Date(selectedDate);
              d.setDate(d.getDate() - 1);
              setSelectedDate(d.toISOString().split('T')[0]);
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <TextField
            type="date"
            size="small"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            sx={{ width: 180 }}
            InputProps={{ sx: { fontWeight: 'bold' } }}
          />
          <IconButton
            size="small"
            onClick={() => {
              const d = new Date(selectedDate);
              d.setDate(d.getDate() + 1);
              setSelectedDate(d.toISOString().split('T')[0]);
            }}
          >
            <ChevronRightIcon />
          </IconButton>
          <Button
            size="small"
            variant="outlined"
            startIcon={<TodayIcon />}
            onClick={() => {
              const shopNow = nowInShopTimezone(shop!.timezone);
              const y = shopNow.getFullYear();
              const m = String(shopNow.getMonth() + 1).padStart(2, '0');
              const d = String(shopNow.getDate()).padStart(2, '0');
              setSelectedDate(`${y}-${m}-${d}`);
            }}
          >
            今日
          </Button>
        </Paper>
      )}

      {/* 状況サマリー */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">{availableTables.length}</Typography>
              <Typography variant="body2" color="text.secondary">空席</Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.main">{occupiedTables.length}</Typography>
              <Typography variant="body2" color="text.secondary">使用中</Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main">{reservedTables.length}</Typography>
              <Typography variant="body2" color="text.secondary">予約済</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* 選択日の予約（席未アサイン） */}
      {unassignedReservations.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            席が未割当の予約があります（{unassignedReservations.length}件）
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {unassignedReservations.map(r => (
              <Chip
                key={r.id}
                icon={<PersonIcon />}
                label={`${r.startTime} ${r.userName}様 (${r.numberOfPeople || 1}名)`}
                onClick={() => {
                  setAssignReservation(r);
                  setOpenAssignDialog(true);
                }}
                color="warning"
                variant="outlined"
              />
            ))}
          </Box>
        </Alert>
      )}

      {/* タブ */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TableBarIcon fontSize="small" />
                席状況 ({tables.length})
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon fontSize="small" />
                席設定
              </Box>
            }
          />
        </Tabs>
      </Paper>

      {/* 席状況タブ */}
      <TabPanel value={tabValue} index={0}>
        {tables.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              席（部屋や教室）が登録されていません
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {tables.map((table) => {
              const tableOrders = getOrdersForTable(table.id);
              const tableTotal = getTableTotal(table.id);
              const sessionStart = table.sessionStartedAt as Date | null;
              const reservation = getReservationForTable(table.id);
              const status = table.status || 'available';

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={table.id}>
                  <Card sx={{
                    height: '100%',
                    borderLeft: 4,
                    borderColor: `${getStatusColor(status)}.main`,
                  }}>
                    <CardContent sx={{ pb: 1 }}>
                      {/* ヘッダー */}
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1,
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TableBarIcon sx={{ fontSize: 28, color: `${getStatusColor(status)}.main` }} />
                          <Box>
                            <Typography variant="h6" lineHeight={1.2}>{table.tableName || table.tableNumber}</Typography>
                            {table.tableName && (
                              <Typography variant="caption" color="text.secondary">No.{table.tableNumber}</Typography>
                            )}
                          </Box>
                        </Box>
                        <Chip
                          label={getStatusLabel(status)}
                          color={getStatusColor(status) as any}
                          size="small"
                        />
                      </Box>

                      <Typography variant="caption" color="text.secondary">
                        {table.seats}人席
                      </Typography>

                      {/* 使用中の場合：滞在時間と注文情報 */}
                      {status === 'occupied' && (
                        <Box sx={{ mt: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <AccessTimeIcon fontSize="small" sx={{ color: getElapsedTimeColor(sessionStart) }} />
                            <Typography sx={{ color: getElapsedTimeColor(sessionStart), fontWeight: 'bold' }}>
                              {formatElapsedTime(sessionStart)}
                            </Typography>
                            {sessionStart && sessionStart instanceof Date && !isNaN(sessionStart.getTime()) && (
                              <Typography variant="caption" color="text.secondary">
                                ({sessionStart.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}〜)
                              </Typography>
                            )}
                          </Box>
                          {/* 担当スタッフ表示 */}
                          {(table as any).primaryStaffName && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <PersonIcon fontSize="small" sx={{ color: 'info.main', fontSize: 16 }} />
                              <Typography variant="body2" sx={{ color: 'info.main', fontWeight: 'bold' }}>
                                {(table as any).primaryStaffName}
                              </Typography>
                            </Box>
                          )}
                          {tableOrders.length > 0 && (
                            <Box sx={{ bgcolor: 'grey.50', p: 1, borderRadius: 1, mb: 1 }}>
                              <Typography variant="body2">
                                注文 {tableOrders.length}件 / ¥{tableTotal.toLocaleString()}
                              </Typography>
                              {tableOrders.flatMap(o => o.items).slice(0, 3).map((item, idx) => (
                                <Typography key={idx} variant="caption" display="block" color="text.secondary">
                                  {item.productName} x{item.quantity}
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* 予約済みの場合：予約情報と来店ボタン */}
                      {status === 'reserved' && reservation && (
                        <Box sx={{ mt: 1, bgcolor: 'warning.50', p: 1, borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <EventAvailableIcon fontSize="small" color="warning" />
                            <Typography variant="body2" fontWeight="bold">
                              {reservation.startTime}〜
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <PersonIcon fontSize="small" />
                            <Typography variant="body2">
                              {reservation.userName}様 ({reservation.numberOfPeople || 1}名)
                            </Typography>
                          </Box>
                          {reservation.userPhone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PhoneIcon fontSize="small" sx={{ fontSize: 14 }} />
                              <Typography variant="caption">{reservation.userPhone}</Typography>
                            </Box>
                          )}
                          {!reservation.checkedInAt && (
                            <Button
                              variant="contained"
                              size="small"
                              color="success"
                              startIcon={<LoginIcon />}
                              onClick={() => handleCheckIn(reservation)}
                              sx={{ mt: 1, width: '100%' }}
                            >
                              来店
                            </Button>
                          )}
                        </Box>
                      )}

                      {/* アクションボタン */}
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                        {status === 'occupied' && (
                          <>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<PointOfSaleIcon />}
                              color="success"
                              onClick={() => handleCheckoutClick(table)}
                            >
                              会計
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleChangeTableStatus(table.id, 'available')}
                            >
                              空席に戻す
                            </Button>
                          </>
                        )}
                        {(status === 'available' || status === 'cleaning') && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleChangeTableStatus(table.id, 'occupied')}
                          >
                            使用開始
                          </Button>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handlePrintQR(table)}
                          title="QR印刷"
                        >
                          <QrCodeIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </TabPanel>

      {/* 席設定タブ */}
      <TabPanel value={tabValue} index={1}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            各席にQRコードを印刷して設置してください。お客様がQRコードをスキャンすることで、モバイル注文が可能になります。
          </Typography>
        </Alert>

        <Grid container spacing={2}>
        {tables.length === 0 ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                席（部屋や教室）が登録されていません
              </Typography>
            </Paper>
          </Grid>
        ) : (
          tables.map((table) => {
            const seatTypeLabel = SEAT_TYPES.find(t => t.value === table.seatType)?.label || 'テーブル席';
            return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={table.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TableBarIcon sx={{ fontSize: 40, color: 'primary.main', mr: 1 }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h5">
                        {table.tableName || table.tableNumber}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {table.tableName ? `(${table.tableNumber}) ` : ''}
                        {table.minCapacity || 1}〜{table.maxCapacity || table.seats}名
                      </Typography>
                    </Box>
                    <Chip
                      label={`#${(table as any).sortOrder || 0}`}
                      size="small"
                      variant="outlined"
                      sx={{ ml: 1 }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
                    <Chip
                      label={seatTypeLabel}
                      color="primary"
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={table.isActive ? '使用可' : '使用不可'}
                      color={table.isActive ? 'success' : 'default'}
                      size="small"
                    />
                    {table.canCombine && (
                      <Chip
                        icon={<LinkIcon />}
                        label="結合可"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>

                  {/* 特徴の表示 */}
                  {table.features && table.features.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
                      {table.features.map((featureCode) => {
                        const feature = SEAT_FEATURES.find(f => f.value === featureCode);
                        return feature ? (
                          <Chip
                            key={featureCode}
                            icon={feature.icon as React.ReactElement}
                            label={feature.label}
                            size="small"
                            variant="outlined"
                          />
                        ) : null;
                      })}
                    </Box>
                  )}

                  {/* 結合情報 */}
                  {table.canCombine && table.combinableWith && table.combinableWith.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      結合可能: {table.combinableWith.map(id => {
                        const t = tables.find(t => t.id === id);
                        return t?.tableNumber || id;
                      }).join(', ')}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <IconButton
                      size="small"
                      onClick={() => handleShowQR(table)}
                      color="primary"
                      title="QRコード表示"
                    >
                      <QrCodeIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handlePrintQR(table)}
                      color="primary"
                      title="印刷"
                    >
                      <PrintIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(table)}
                      color="primary"
                      title="編集"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDeleteTarget(table);
                        setOpenDeleteDialog(true);
                      }}
                      color="error"
                      title="削除"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            );
          })
        )}
        </Grid>
      </TabPanel>

      {/* 編集/追加ダイアログ */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTable ? '席を編集' : '席を追加'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* 基本情報 */}
            <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>
              基本情報
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <TextField
                  label="席番号"
                  value={formData.tableNumber}
                  onChange={(e) => setFormData({ ...formData, tableNumber: e.target.value })}
                  required
                  fullWidth
                  placeholder="例: 1, A1"
                  helperText="内部管理用の識別番号"
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  label="表示名（任意）"
                  value={formData.tableName}
                  onChange={(e) => setFormData({ ...formData, tableName: e.target.value })}
                  fullWidth
                  placeholder="例: 窓際席A"
                  helperText="お客様に表示する名称"
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  label="表示順"
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData({ ...formData, sortOrder: Number(e.target.value) })}
                  fullWidth
                  inputProps={{ min: 0 }}
                />
              </Grid>
            </Grid>

            <FormControl fullWidth>
              <InputLabel>席種別</InputLabel>
              <Select
                value={formData.seatType}
                onChange={(e) => setFormData({ ...formData, seatType: e.target.value as SeatType })}
                label="席種別"
              >
                {SEAT_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="最小人数"
                  type="number"
                  value={formData.minCapacity}
                  onChange={(e) => setFormData({ ...formData, minCapacity: Number(e.target.value) })}
                  required
                  fullWidth
                  inputProps={{ min: 1, max: 100 }}
                  helperText="予約可能な最小人数"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="最大人数"
                  type="number"
                  value={formData.maxCapacity}
                  onChange={(e) => setFormData({ ...formData, maxCapacity: Number(e.target.value) })}
                  required
                  fullWidth
                  inputProps={{ min: 1, max: 100 }}
                  helperText="予約可能な最大人数"
                />
              </Grid>
            </Grid>

            {/* 席の特徴 */}
            <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>
              席の特徴
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {SEAT_FEATURES.map((feature) => (
                <Chip
                  key={feature.value}
                  icon={feature.icon as React.ReactElement}
                  label={feature.label}
                  onClick={() => {
                    const newFeatures = formData.features.includes(feature.value)
                      ? formData.features.filter(f => f !== feature.value)
                      : [...formData.features, feature.value];
                    setFormData({ ...formData, features: newFeatures });
                  }}
                  color={formData.features.includes(feature.value) ? 'primary' : 'default'}
                  variant={formData.features.includes(feature.value) ? 'filled' : 'outlined'}
                />
              ))}
            </Box>

            <TextField
              label="説明"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="例: その他の特徴や注意事項"
              helperText="席の特徴や注意事項（任意）"
            />

            <TextField
              label="追加料金"
              type="number"
              value={formData.additionalFee}
              onChange={(e) => setFormData({ ...formData, additionalFee: e.target.value })}
              fullWidth
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>¥</Typography>,
              }}
              helperText="この席を指定時の追加料金（円）"
            />

            {/* 結合設定 */}
            <Typography variant="subtitle2" color="primary" sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinkIcon fontSize="small" />
              テーブル結合設定
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.canCombine}
                  onChange={() => setFormData({ ...formData, canCombine: !formData.canCombine })}
                />
              }
              label="結合可能"
            />

            {formData.canCombine && (
              <Box sx={{ pl: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>結合可能なテーブル</InputLabel>
                  <Select
                    multiple
                    value={formData.combinableWith}
                    onChange={(e) => setFormData({
                      ...formData,
                      combinableWith: typeof e.target.value === 'string'
                        ? e.target.value.split(',')
                        : e.target.value
                    })}
                    label="結合可能なテーブル"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((id) => {
                          const table = tables.find(t => t.id === id);
                          return (
                            <Chip key={id} label={table?.tableNumber || id} size="small" />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {tables
                      .filter(t => t.id !== editingTable?.id)
                      .map((table) => (
                        <MenuItem key={table.id} value={table.id}>
                          {table.tableNumber} ({table.seats}人席)
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>

                <Typography variant="body2" color="text.secondary">
                  2テーブル結合時
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="最小人数"
                      type="number"
                      value={formData.combine2MinCapacity || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        combine2MinCapacity: e.target.value ? Number(e.target.value) : undefined
                      })}
                      fullWidth
                      inputProps={{ min: 1, max: 100 }}
                      placeholder="例: 5"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="最大人数"
                      type="number"
                      value={formData.combine2MaxCapacity || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        combine2MaxCapacity: e.target.value ? Number(e.target.value) : undefined
                      })}
                      fullWidth
                      inputProps={{ min: 1, max: 100 }}
                      placeholder="例: 10"
                    />
                  </Grid>
                </Grid>

                <Typography variant="body2" color="text.secondary">
                  3テーブル以上結合時
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="最小人数"
                      type="number"
                      value={formData.combine3PlusMinCapacity || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        combine3PlusMinCapacity: e.target.value ? Number(e.target.value) : undefined
                      })}
                      fullWidth
                      inputProps={{ min: 1, max: 100 }}
                      placeholder="例: 8"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="最大人数"
                      type="number"
                      value={formData.combine3PlusMaxCapacity || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        combine3PlusMaxCapacity: e.target.value ? Number(e.target.value) : undefined
                      })}
                      fullWidth
                      inputProps={{ min: 1, max: 100 }}
                      placeholder="例: 14"
                    />
                  </Grid>
                </Grid>

                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="caption">
                    結合時の人数は、テーブルの形状によって異なります。
                    端なしテーブルは結合で人数が増え、端ありテーブルは減る場合もあります。
                  </Typography>
                </Alert>
              </Box>
            )}

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="caption">
                席を追加すると、自動的にQRコードが生成されます。
                モバイルオーダーを使う場合はQRコードを印刷して席に設置してください。
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingTable ? '更新' : '追加'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QRコード表示ダイアログ */}
      <Dialog open={openQRDialog} onClose={() => setOpenQRDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          QRコード - {selectedTable?.tableName || selectedTable?.tableNumber}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            {selectedTable ? (
              <>
                <div id="qr-code-print-source">
                  <QRCodeSVG
                    value={getMobileOrderUrl(selectedTable.id)}
                    size={300}
                    level="M"
                  />
                </div>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  お客様はこのQRコードをスキャンして注文できます
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Scan this QR code to place an order
                </Typography>

                {/* URL表示エリア */}
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    モバイルオーダーURL
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={getMobileOrderUrl(selectedTable.id)}
                      InputProps={{
                        readOnly: true,
                        sx: { fontSize: '0.75rem', bgcolor: 'white' }
                      }}
                    />
                    <IconButton
                      onClick={() => handleCopyUrl(getMobileOrderUrl(selectedTable.id))}
                      color="primary"
                      title="URLをコピー"
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Box>
                </Box>
              </>
            ) : (
              <Typography>QRコードが生成されていません</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenQRDialog(false)}>閉じる</Button>
          <Button
            onClick={handlePrintQRBrowser}
            variant="contained"
            startIcon={<QrCodeIcon />}
          >
            印刷
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>席を削除</DialogTitle>
        <DialogContent>
          <Typography>
            「{deleteTarget?.tableName || deleteTarget?.tableNumber}」を削除してもよろしいですか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>キャンセル</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            削除
          </Button>
        </DialogActions>
      </Dialog>


      {/* 席アサインダイアログ */}
      <Dialog open={openAssignDialog} onClose={() => setOpenAssignDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          席アサイン
        </DialogTitle>
        <DialogContent>
          {assignReservation && (
            <Box sx={{ mt: 2 }}>
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" color="text.secondary">予約情報</Typography>
                <Typography variant="h6">{assignReservation.userName}様</Typography>
                <Typography variant="body2">
                  {assignReservation.startTime}〜 / {assignReservation.numberOfPeople || 1}名
                </Typography>
                {assignReservation.menuName && (
                  <Typography variant="caption" color="text.secondary">
                    {assignReservation.menuName}
                  </Typography>
                )}
              </Paper>

              <FormControl fullWidth>
                <InputLabel>席を選択</InputLabel>
                <Select
                  value={selectedAssignTableId}
                  onChange={(e) => setSelectedAssignTableId(e.target.value)}
                  label="席を選択"
                >
                  {availableTables.map((table) => (
                    <MenuItem key={table.id} value={table.id}>
                      {table.tableNumber} ({table.seats}人席)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {availableTables.length === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  空席がありません
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenAssignDialog(false);
            setAssignReservation(null);
            setSelectedAssignTableId('');
          }}>
            キャンセル
          </Button>
          <Button
            onClick={handleAssignTable}
            variant="contained"
            disabled={!selectedAssignTableId}
          >
            アサイン
          </Button>
        </DialogActions>
      </Dialog>

      {/* 注文なし会計確認ダイアログ */}
      <Dialog open={openCheckoutConfirmDialog} onClose={() => setOpenCheckoutConfirmDialog(false)}>
        <DialogTitle>会計確認</DialogTitle>
        <DialogContent>
          <Typography>
            {checkoutTargetTable?.tableNumber}には未会計の注文がありません。
          </Typography>
          <Typography sx={{ mt: 1 }}>
            このまま席を空席にしますか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenCheckoutConfirmDialog(false);
            setCheckoutTargetTable(null);
          }}>
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmCheckoutWithoutOrders}
            variant="contained"
            color="primary"
          >
            空席にする
          </Button>
        </DialogActions>
      </Dialog>

      {/* 来店確認ダイアログ（担当スタッフ選択付き） */}
      <Dialog open={openCheckInDialog} onClose={() => setOpenCheckInDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>来店確認</DialogTitle>
        <DialogContent>
          {checkInReservation && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography fontWeight="bold">{checkInReservation.userName}様</Typography>
              <Typography variant="body2" color="text.secondary">
                {checkInReservation.numberOfPeople || 1}名 / {checkInReservation.startTime}〜
              </Typography>
              {(checkInReservation as any).menuName && (
                <Typography variant="body2" color="text.secondary">
                  メニュー: {(checkInReservation as any).menuName}
                </Typography>
              )}
            </Box>
          )}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>担当スタッフ</Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={checkInStaffId}
            onChange={(e) => {
              const id = e.target.value;
              setCheckInStaffId(id);
              const staff = staffMembers.find(s => s.id === id);
              setCheckInStaffName(staff?.name || '');
            }}
            sx={{ mb: 2 }}
          >
            <MenuItem value="">店</MenuItem>
            {staffMembers.map((staff) => (
              <MenuItem key={staff.id} value={staff.id}>{staff.name}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenCheckInDialog(false);
            setCheckInReservation(null);
          }}>
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmCheckIn}
            variant="contained"
            color="success"
            startIcon={<LoginIcon />}
          >
            来店開始
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Tables;
